import { IEntry } from "core-integration/src/store/entries/types";
import { Button } from "plos/src/components/buttons/Button";
import Dialog from "plos/src/components/Dialog";
import AiIcon from "plos/src/img/icons/ai.svg?react";
import { contentMain } from "plos/src/layouts/layouts.css";
import { useTranslation } from "react-i18next";
import { useAdobeContext } from "../../../contexts/AdobeContext";
import seasonRecapModal from "../../../img/adobe/adobe-season-modal.png";
import summaryModal from "../../../img/adobe/summary-modal.png";
import {
  adobeImageContainer,
  adobePatternContainer,
  adobeTemplateButtonContainer,
  adobeTemplateImg,
} from "./adobeTemplateModal.css";

interface AdobeTemplateModalProps {
  entry: IEntry;
  isOpen: boolean;
  eventId: number;
  handleClose: () => void;
  adobeGameweekTemplateUrl?: string;
  adobeSeasonTemplateUrl?: string;
  hasSeenGWModal?: boolean;
}

const AdobeTemplateModal = ({
  entry,
  isOpen,
  eventId,
  handleClose,
  adobeGameweekTemplateUrl,
  adobeSeasonTemplateUrl,
  hasSeenGWModal = false,
}: AdobeTemplateModalProps) => {
  const { t } = useTranslation();
  const { embed } = useAdobeContext();

  const bothTemplatesAvailable =
    adobeGameweekTemplateUrl && adobeSeasonTemplateUrl;

  const modalText =
    bothTemplatesAvailable && !hasSeenGWModal
      ? t(
          "gameweekAndSeasonRecapsReady",
          "Your Gameweek {{eventId}} and Season Recaps are ready!",
          { eventId },
        )
      : adobeSeasonTemplateUrl
        ? t("seasonRecapReady", "Your Season Recap is ready!")
        : t("gameweekRecapReady", "Your Gameweek {{eventId}} Recap is ready!", {
            eventId,
          });

  if (!adobeGameweekTemplateUrl && !adobeSeasonTemplateUrl) return null;

  return (
    <Dialog
      handleClose={handleClose}
      isOpen={isOpen}
      headerVariant="gradient"
      label={t("adobeRecapModalLabel", "Adobe Recap Modal")}
    >
      <div className={adobeImageContainer}>
        <div className={adobePatternContainer}>
          <img
            src={adobeSeasonTemplateUrl ? seasonRecapModal : summaryModal}
            className={adobeTemplateImg}
            alt={t("adobeSummaryAltText", "Adobe x FPL {{type}} Summary", {
              type: adobeSeasonTemplateUrl ? "Season" : "Gameweek",
            })}
          />
        </div>
      </div>
      <div className={contentMain}>
        <div>
          <h3>{modalText}</h3>
        </div>
        <div className={adobeTemplateButtonContainer}>
          {adobeGameweekTemplateUrl && !hasSeenGWModal && (
            <Button
              onPress={() => {
                handleClose();
                embed.viewImage({
                  badgeHolder: Boolean(
                    entry.club_badge_src && entry.club_badge_src !== "Pending",
                  ),
                  entryPoint: "in-app-modal",
                  url: adobeGameweekTemplateUrl,
                  type: "gameweek",
                });
              }}
              fullWidth
            >
              <AiIcon height={18} width={18} />
              {t("viewGameweekRecap", "View Gameweek Recap")}
            </Button>
          )}
          {adobeSeasonTemplateUrl && (
            <Button
              onPress={() => {
                handleClose();
                embed.viewImage({
                  badgeHolder: Boolean(
                    entry.club_badge_src && entry.club_badge_src !== "Pending",
                  ),
                  entryPoint: "in-app-modal",
                  url: adobeSeasonTemplateUrl,
                  type: "season",
                });
              }}
              fullWidth
            >
              <AiIcon height={18} width={18} />
              {t("viewSeasonRecap", "View Season Recap")}
            </Button>
          )}
        </div>
        <Button
          appearance="outlined"
          onPress={() => {
            handleClose();
          }}
        >
          {t("dismiss", "Dismiss")}
        </Button>
      </div>
    </Dialog>
  );
};

export default AdobeTemplateModal;
